import type { FilterDefinition } from './declarations/type';

/**
 * Whether a filter currently narrows the table. Built-in types derive it from
 * their value; `isSet` on the definition overrides.
 *
 * @example
 * isFilterSet({ type: 'text', value: 'Billa', ... }) // true
 */
export function isFilterSet(filter: FilterDefinition<any>): boolean {
  if (filter.isSet) {
    return filter.isSet();
  }
  switch (filter.type) {
    case 'dateRange':
    case 'numberRange':
      return Boolean(filter.range?.from || filter.range?.to);
    case 'boolean':
      return filter.value;
    case 'text':
    case 'select':
      return Boolean(filter.value);
    default:
      return false;
  }
}

/**
 * Clears a filter. Built-in types call their `onChange` with nothing; `unset`
 * on the definition overrides. Returns false when the filter cannot be cleared.
 *
 * @example
 * unsetFilter(dateFilter) // dateFilter.onChange(undefined)
 */
export function unsetFilter(filter: FilterDefinition<any>): boolean {
  if (filter.unset) {
    filter.unset();
    return true;
  }
  switch (filter.type) {
    case 'dateRange':
    case 'numberRange':
    case 'text':
    case 'select':
      filter.onChange(undefined);
      return true;
    case 'boolean':
      filter.onChange(false);
      return true;
    default:
      return false;
  }
}

export const canUnsetFilter = (filter: FilterDefinition<any>): boolean =>
  Boolean(filter.unset) || filter.type !== 'custom';
